/**
 * Menu background music. Loops through HTMLAudio (latency is irrelevant),
 * follows the settings volume, and pauses when the tab is hidden.
 *
 * The `public/audio/music/menu-loop.ogg` asset is generated with Suno (paid
 * plan, commercial use) — if missing, the component stays silent without
 * breaking anything.
 */

import { createEffect, onCleanup, onMount } from "solid-js";
import { resolveAssetUrl } from "@melodimals/game-core";
import { useSettings } from "@/providers/settings-provider";
import { useAudio } from "@/providers/audio-provider";

/** Attenuation while narration is speaking (ducking). */
const DUCK_FACTOR = 0.25;

const MUSIC_URL = "audio/music/menu-loop.ogg";

export function BackgroundMusic() {
  const settings = useSettings();
  const { narrationSpeaking } = useAudio();
  let audio: HTMLAudioElement | null = null;
  let unlocked = false;

  const tryPlay = () => {
    if (audio && settings.musicVolume() > 0) {
      audio.play().catch(() => {
        // Autoplay blocked until the first gesture — `unlock` retries it.
      });
    }
  };

  onMount(() => {
    audio = new Audio(resolveAssetUrl(MUSIC_URL));
    audio.loop = true;
    audio.volume = settings.musicVolume();
    audio.addEventListener("error", () => {
      // Missing asset (not generated yet): silence.
      audio = null;
    });

    const unlock = () => {
      if (!unlocked) {
        unlocked = true;
        tryPlay();
      }
    };
    const onVisibility = () => {
      if (document.hidden) {
        audio?.pause();
      } else if (unlocked) {
        tryPlay();
      }
    };
    document.addEventListener("pointerdown", unlock, { capture: true, passive: true });
    document.addEventListener("visibilitychange", onVisibility);

    onCleanup(() => {
      document.removeEventListener("pointerdown", unlock, { capture: true });
      document.removeEventListener("visibilitychange", onVisibility);
      audio?.pause();
      audio = null;
    });
  });

  createEffect(() => {
    const volume = settings.musicVolume() * (narrationSpeaking() ? DUCK_FACTOR : 1);
    if (audio) {
      audio.volume = volume;
      if (volume === 0) {
        audio.pause();
      } else if (unlocked && audio.paused && !document.hidden) {
        tryPlay();
      }
    }
  });

  return null;
}
